import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { gql, useQuery } from '@apollo/client';
import cloneDeep from 'lodash/cloneDeep';
import widgetSchemas from 'shared/widgets/schemas';
import { fetchCustomWidgets, fetchCustomWidgetBody } from '../ComponentBrowser';
import { S, DARK, labelStyle } from './shellTokens';
import SectionPeek, { PeekItem } from './SectionPeek';
import { Band, bandAround, headingToward, outsideBand } from './hoverIntent';

// How long the pointer rests on a row before the open preview switches to it.
const DWELL_MS = 220;

/** What the editor inserts: the same shape the widget browsers pass to onSelect. */
export type NewComponent = { presetId: string | null; widgetId: string; props: Record<string, any> }; // eslint-disable-line @typescript-eslint/no-explicit-any
export type Preset = { _id: string; title: string; widgetId: string; props?: Record<string, any> }; // eslint-disable-line @typescript-eslint/no-explicit-any

export const PRESET_USAGE = gql`
  query WidgetPresetUsage { widgetPresetUsage { presetId pages } }
`;

/** What's being dragged from the panel. The type marks the drag, so the page only takes these. */
export type DragItem = { title: string; add: () => unknown };
export const INSERT_DRAG_TYPE = 'application/x-gx-insert';

type CustomSummary = { _id: string; name: string; title?: string; description?: string; category?: string; publishedVersion?: string | null };
type Schema = { id: string; title: string; defaultProps?: Record<string, any> }; // eslint-disable-line @typescript-eslint/no-explicit-any

type Props = {
  /** Where the section goes, e.g. "after “Hero”" or "at the end of Body". */
  placement: string;
  /** The place was picked on the canvas: shown highlighted, with a way back to the usual place. */
  targeted?: boolean;
  onClearPlacement?: () => void;
  /** Changes when the panel is opened from the canvas: the cursor goes to the search field. */
  focusKey?: number;
  /** A row is being dragged onto the page (null when the drag ends): `add` adds it, wherever the editor says. */
  onDragItem?: (item: DragItem | null) => void;
  /** The page being edited: previews render inside its template. */
  pageId: string;
  presets?: Preset[];
  onInsert: (component: NewComponent, options?: { edit?: boolean }) => void;
};

const AI_WIDGET = 'AiGeneratedWidget';
// Its preview: there's nothing to show until the code from an AI tool is pasted in.
const AI_BLANK = {
  icon: 'auto_awesome',
  title: 'Starts empty',
  text: 'Add it, then paste the code from your AI tool (Claude, ChatGPT and others). The section shows what you paste.'
};
// Resting on a row's preview (eye) button this long starts loading its preview out of sight.
const PRELOAD_MS = 120;
// While a preview is open, resting on another row switches the preview to that row (keyboard focus does too).
// Set to false to keep the preview on the widget whose eye was clicked until another eye is clicked.
const FOLLOW_HOVER = true;
/** A new, empty AI section: added, then filled from the paste box in the full editor. */
export const aiSection = (): NewComponent => {
  const schema = (widgetSchemas as Record<string, Schema>)[AI_WIDGET];
  return { presetId: null, widgetId: AI_WIDGET, props: (schema && schema.defaultProps) || {} };
};
const schemas = (): Schema[] => Object.keys(widgetSchemas)
  .map(k => (widgetSchemas as Record<string, Schema>)[k])
  .filter(s => s && s.id !== 'CustomWidget' && s.id !== AI_WIDGET);

/**
 * A preset section. Presets replace the section's props when it renders; the
 * copy kept on the section is what it shows if the preset is later detached.
 */
export const presetInsert = (preset: Preset): NewComponent => ({
  presetId: String(preset._id),
  widgetId: preset.widgetId,
  props: cloneDeep(preset.props || {})
});

/**
 * Builds a custom widget insert exactly like the widget browser does: the
 * published version's defaultProps, following the latest published version.
 */
export const customWidgetInsert = async (name: string, publishedVersion?: string | null): Promise<NewComponent> => {
  const body = await fetchCustomWidgetBody(name, publishedVersion);
  const defaultProps = (body && body.defaultProps) || {};
  return {
    presetId: null,
    widgetId: 'CustomWidget',
    props: {
      customWidgetName: name,
      // Empty = follow the latest published version (see customWidgetVersions.js).
      customWidgetVersion: '',
      widgetProps: defaultProps,
      ...defaultProps
    }
  };
};

const matches = (query: string, ...texts: Array<string | undefined>) => {
  const q = query.trim().toLowerCase();
  return !q || texts.some(t => (t || '').toLowerCase().includes(q));
};

const useStyles = createUseStyles({
  // Opened from the page: the picked place fades in once, enough for the eye to land on it.
  '@keyframes gxPlaceIn': { from: { opacity: 0.35 }, to: { opacity: 1 } },
  flash: { animation: '$gxPlaceIn 450ms ease-out' },
  root: { display: 'grid', gap: 16, fontFamily: S.ui, color: S.fg },
  placement: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: S.muted, marginTop: -6, minHeight: 22 },
  // Picked on the canvas: easy to notice, so a click on "+" visibly lands here.
  targeted: {
    marginTop: -4,
    padding: '3px 4px 3px 8px',
    borderRadius: 6,
    background: S.claySoft,
    color: S.clayInk,
    fontWeight: 600
  },
  placementText: { flex: 1, minWidth: 0 },
  placementClear: {
    all: 'unset',
    cursor: 'pointer',
    display: 'inline-flex',
    borderRadius: 4,
    padding: 2,
    '& .material-icons': { fontSize: 16 },
    '&:hover': { background: 'rgba(0, 0, 0, 0.06)' },
    '&:focus-visible': { outline: `2px solid ${S.fg}` }
  },
  paste: {
    all: 'unset',
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'grid',
    gap: 2,
    padding: '12px 14px',
    borderRadius: 8,
    border: `1px solid ${S.clay}`,
    background: '#FBF6F0',
    color: S.clayInk,
    '&:hover, &:focus-visible': { background: '#F6EBDD' },
    [DARK]: { background: S.claySoft, '&:hover, &:focus-visible': { background: S.chromeHover } }
  },
  pasteTitle: { fontWeight: 600, fontSize: 14 },
  pasteText: { fontSize: 12 },
  search: {
    boxSizing: 'border-box',
    width: '100%',
    padding: '8px 10px',
    fontSize: 14,
    fontFamily: S.ui,
    border: `1px solid ${S.fieldLine}`,
    borderRadius: 6,
    background: S.field,
    outline: 'none',
    '&:focus': { borderColor: S.fg }
  },
  group: { display: 'grid', gap: 2 },
  label: { ...labelStyle, display: 'flex', justifyContent: 'space-between', marginBottom: 2 },
  count: { fontFamily: S.mono, fontSize: 11, fontWeight: 400, letterSpacing: 0 },
  // A row: the item (click to add) and an eye (click to preview).
  rowWrap: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    alignItems: 'center',
    borderRadius: 6,
    // Hover, or keyboard focus on the row: not focus left behind by a mouse click (that kept an old row lit).
    '&:hover': { background: S.chromeHover },
    '&:hover $eye, & $row:focus-visible + $eye': { opacity: 1 }
  },
  peeking: {
    background: S.field,
    boxShadow: `inset 0 0 0 1px ${S.fieldLine}, inset 3px 0 0 ${S.fg}`,
    '&:hover': { background: S.field },
    '& $eye': { opacity: 1, color: S.fg }
  },
  row: {
    all: 'unset',
    boxSizing: 'border-box',
    cursor: 'pointer',
    minWidth: 0,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: 8,
    alignItems: 'baseline',
    padding: '7px 4px 7px 8px',
    borderRadius: 6,
    fontSize: 14,
    '&:focus-visible': { boxShadow: `inset 0 0 0 2px ${S.fg}` },
    '&:disabled': { color: S.muted, cursor: 'wait' }
  },
  eye: {
    all: 'unset',
    cursor: 'pointer',
    width: 28,
    height: 28,
    marginRight: 2,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 5,
    color: S.muted,
    opacity: 0,
    transition: 'opacity 120ms ease',
    '& .material-icons': { fontSize: 18 },
    '&:hover, &:focus-visible': { color: S.fg, background: S.line, opacity: 1 },
    // No hover on touch screens: always show it.
    '@media (hover: none)': { opacity: 1 }
  },
  rowTitle: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rowMeta: { fontFamily: S.mono, fontSize: 11, color: S.muted },
  quiet: { fontSize: 13, color: S.muted, padding: '4px 8px' },
  dragHint: { fontSize: 12, color: S.muted, marginTop: -10 }
});

const InsertPanel: React.FC<Props> = ({ placement, targeted, onClearPlacement, focusKey, onDragItem, pageId, presets = [], onInsert }) => {
  const c = useStyles();
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  // Opened from the page: the cursor goes to the search, and the picked place fades in once.
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (!focusKey) return undefined;
    if (searchRef.current) searchRef.current.focus();
    setFlash(true);
    const t = window.setTimeout(() => setFlash(false), 1600);
    return () => window.clearTimeout(t);
  }, [focusKey]);
  const [custom, setCustom] = useState<CustomSummary[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // The item being previewed. While a preview is open, hovering or focusing another row switches it.
  const [peek, setPeek] = useState<(PeekItem & { add: () => void }) | null>(null);
  // The preview loading out of sight: the item whose preview button the pointer rests on (or the last one
  // closed), so opening it is immediate. Each preview is a fresh sandboxed page (no shared cache), so this
  // only starts on the preview button, a clear sign of intent, not on every row passed over.
  const [pre, setPre] = useState<(PeekItem & { add: () => void }) | null>(null);
  const preTimer = useRef<number | null>(null);
  const preKey = useRef<string | null>(null);
  const root = useRef<HTMLDivElement>(null);
  // The item whose eye was clicked to show the preview. Hovering can switch the preview to another row; a
  // click on that row's eye then keeps it open (it's what the person went there for), and only a click on
  // the eye of the item already chosen closes it.
  const clickedKey = useRef<string | null>(null);
  const closePeek = useCallback(() => {
    // Keep the closed one loaded out of sight: opening it again is immediate.
    if (peekRef.current) setPre(peekRef.current);
    setPeek(null);
  }, []);

  // Hover follows intent, not contact: a row has to be rested on (DWELL_MS), rows crossed on the way
  // to the preview don't count, and right after a click small drifts off the clicked row are ignored.
  const peekRef = useRef(peek);
  peekRef.current = peek;
  const hovered = useRef<{ item: PeekItem; add: () => void } | null>(null);
  const pointer = useRef<Array<{ x: number; y: number; t: number }>>([]);
  const band = useRef<Band | null>(null);
  const timer = useRef<number | null>(null);
  const previewEdge = () => {
    const el = document.querySelector('[data-testid="section-peek"]');
    return el ? el.getBoundingClientRect() : null;
  };
  const trySwitch = () => {
    timer.current = null;
    const h = hovered.current;
    const open = peekRef.current;
    if (!h || !open || open.key === h.item.key) return;
    const pts = pointer.current;
    const last = pts[pts.length - 1];
    if (last && !outsideBand(last.y, band.current)) return;
    const moving = last && Date.now() - last.t < 100;
    if (moving && headingToward(pts[pts.length - 2], last, previewEdge())) {
      timer.current = window.setTimeout(trySwitch, DWELL_MS);
      return;
    }
    setPeek({ ...h.item, add: h.add });
  };
  const schedule = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(trySwitch, DWELL_MS);
  };
  useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    if (preTimer.current !== null) window.clearTimeout(preTimer.current);
  }, []);
  // Scrolling the list is deliberate, and moves the clicked row: drop the drift band.
  useEffect(() => {
    const scroller = root.current && root.current.closest('[data-panel]');
    if (!scroller) return undefined;
    const drop = () => { band.current = null; };
    scroller.addEventListener('scroll', drop, true);
    return () => scroller.removeEventListener('scroll', drop, true);
  }, []);
  const onPointerMove = (e: React.MouseEvent) => {
    pointer.current = [...pointer.current.slice(-3), { x: e.clientX, y: e.clientY, t: Date.now() }];
    if (band.current && outsideBand(e.clientY, band.current)) {
      band.current = null;
      if (FOLLOW_HOVER && hovered.current && peekRef.current && hovered.current.item.key !== peekRef.current.key) schedule();
    }
  };

  useEffect(() => {
    let live = true;
    fetchCustomWidgets().then((list: CustomSummary[]) => {
      if (live) setCustom(list.filter(w => w.publishedVersion));
    });
    return () => { live = false; };
  }, []);

  const usage = useQuery<{ widgetPresetUsage: Array<{ presetId: string; pages: number }> }>(PRESET_USAGE, { fetchPolicy: 'cache-and-network' });
  const pagesUsing = useMemo(() => new Map(((usage.data && usage.data.widgetPresetUsage) || []).map(u => [String(u.presetId), u.pages])), [usage.data]);
  const presetsShown = presets.filter(p => matches(query, p.title, p.widgetId, p.props && p.props.customWidgetName))
    .sort((a, b) => a.title.localeCompare(b.title));
  const builtIn = useMemo(schemas, []);
  // Every built-in widget in one list (the old "basic" ones included), A to Z. The AI widget is in it too
  // (people look for it here); it does what the card above does.
  const aiSchema = (widgetSchemas as Record<string, Schema>)[AI_WIDGET];
  const allWidgets = [...builtIn, ...(aiSchema ? [aiSchema] : [])]
    .filter(s => matches(query, s.title, s.id, s.id === AI_WIDGET ? 'paste ai tool claude chatgpt' : ''))
    .sort((a, b) => a.title.localeCompare(b.title));
  const customShown = (custom || []).filter(w => matches(query, w.title, w.name, w.category))
    .sort((a, b) => (a.title || a.name).localeCompare(b.title || b.name));

  // Dragging a row onto the page: the editor shows where it lands and adds it on drop.
  const dragProps = (title: string, add: () => unknown, disabled?: boolean) => (onDragItem && !disabled ? {
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.setData(INSERT_DRAG_TYPE, title);
      setPeek(null);
      onDragItem({ title, add });
    },
    onDragEnd: () => onDragItem(null)
  } : {});

  const insertCustom = async (w: CustomSummary) => {
    setBusy(w.name);
    try {
      onInsert(await customWidgetInsert(w.name, w.publishedVersion));
    } finally {
      setBusy(null);
    }
  };
  const insertBuiltIn = (s: Schema) => onInsert({ presetId: null, widgetId: s.id, props: s.defaultProps || {} });
  const insertAi = () => {
    // The AI section opens its own window; a preview left open would sit behind it.
    if (peekRef.current) closePeek();
    onInsert(aiSection(), { edit: true });
  };

  // Up and down arrows move between rows.
  const moveFocus = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const rows = Array.from((root.current && root.current.querySelectorAll<HTMLButtonElement>('[data-insert-row]')) || []);
    const i = rows.indexOf(e.currentTarget);
    const nextRow = rows[i + (e.key === 'ArrowDown' ? 1 : -1)];
    if (nextRow) { e.preventDefault(); nextRow.focus(); }
  };
  const row = ({ item, meta, add, disabled, data, tooltip }: {
    item: PeekItem; meta?: string; add: () => void; disabled?: boolean; data: Record<string, string>; tooltip?: string;
  }) => {
    const peeking = Boolean(peek && peek.key === item.key);
    // Keyboard focus is deliberate: switch at once. A click also focuses the row, just before it adds the
    // widget; that isn't a request to preview it (the preview would switch, then stay over the page).
    const focusFollow = (e: React.FocusEvent<HTMLButtonElement>) => {
      let keyboard = true;
      try { keyboard = e.currentTarget.matches(':focus-visible'); } catch (err) { /* older browsers: keep switching */ }
      if (keyboard && peek && !peeking) setPeek({ ...item, add });
    };
    // Adding a widget from the list ends the preview, like the preview's own Add button does.
    const addFromList = () => {
      if (peekRef.current) closePeek();
      add();
    };
    // Over/out, not enter/leave: React drops the enter when the element the pointer came from was redrawn
    // under it (the page's hover controls do that), and the row was never noticed. Over also fires moving
    // between the row's own parts; only arriving on the row counts.
    const enter = () => {
      const arriving = !hovered.current || hovered.current.item.key !== item.key;
      hovered.current = { item, add };
      if (FOLLOW_HOVER && arriving && peek && !peeking) schedule();
    };
    const leave = (e: React.MouseEvent<HTMLElement>) => {
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
      if (hovered.current && hovered.current.item.key === item.key) hovered.current = null;
    };
    return (
      <div key={item.key} className={`${c.rowWrap} ${peeking ? c.peeking : ''}`} onMouseOver={enter} onMouseOut={leave} data-row-key={item.key} {...dragProps(item.title, add, disabled)}>
        <button type="button" className={c.row} disabled={disabled} onClick={addFromList} onFocus={focusFollow} onKeyDown={moveFocus} title={tooltip} data-insert-row {...data}>
          <span className={c.rowTitle}>{item.title}</span>
          {meta && <span className={c.rowMeta}>{meta}</span>}
        </button>
        <button
          type="button"
          className={c.eye}
          aria-label={`Preview ${item.title}`}
          aria-pressed={peeking}
          title="Preview"
          data-peek={item.key}
          // Resting on the button loads the preview out of sight (over/out for the reason above; moving onto
          // the icon inside doesn't start it again).
          onMouseOver={() => {
            if (peek || preKey.current === item.key) return;
            if (preTimer.current !== null) window.clearTimeout(preTimer.current);
            preKey.current = item.key;
            preTimer.current = window.setTimeout(() => setPre({ ...item, add }), PRELOAD_MS);
          }}
          onMouseOut={(e) => {
            if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
            if (preTimer.current !== null) window.clearTimeout(preTimer.current);
            preKey.current = null;
          }}
          onFocus={() => { if (!peek) setPre({ ...item, add }); }}
          onClick={(e) => {
            // Closed here too, it stays loaded out of sight.
            if (peeking && clickedKey.current === item.key) { clickedKey.current = null; closePeek(); return; }
            clickedKey.current = item.key;
            const rowEl = (e.currentTarget as HTMLElement).closest('[data-row-key]');
            band.current = rowEl ? bandAround(rowEl.getBoundingClientRect()) : null;
            hovered.current = { item, add };
            setPeek({ ...item, add });
          }}
        >
          <i className="material-icons" aria-hidden>visibility</i>
        </button>
      </div>
    );
  };

  const section = (title: string, count: number, children: React.ReactNode) => (
    <section className={c.group} aria-label={title}>
      <div className={c.label}><span>{title}</span><span className={c.count}>{count}</span></div>
      {children}
    </section>
  );

  return (
    <div className={c.root} data-testid="insert-panel" ref={root} onMouseMove={onPointerMove}>
      <div className={`${c.placement} ${targeted ? c.targeted : ''} ${flash ? c.flash : ''}`} data-testid="insert-placement" data-targeted={targeted || undefined}>
        <span className={c.placementText}>Adds {placement}</span>
        {targeted && onClearPlacement && (
          <button type="button" className={c.placementClear} onClick={onClearPlacement} aria-label="Add in the usual place instead" title="Add in the usual place instead">
            <i className="material-icons" aria-hidden>close</i>
          </button>
        )}
      </div>
      <button type="button" className={c.paste} onClick={insertAi} data-testid="insert-ai" {...dragProps('AI section', insertAi)}>
        <span className={c.pasteTitle}>Paste from your AI tool</span>
        <span className={c.pasteText}>Adds a section and opens it, ready for the reply from your AI tool.</span>
      </button>
      <input
        ref={searchRef}
        className={c.search}
        type="search"
        placeholder="Find a widget"
        aria-label="Find a widget"
        value={query}
        onChange={e => setQuery(e.target.value)}
      />
      {onDragItem && <div className={c.dragHint}>Click to add, or drag onto the page.</div>}
      {section('Custom widgets', customShown.length, (
        <>
          {custom === null && <div className={c.quiet}>Loading…</div>}
          {custom !== null && !customShown.length && <div className={c.quiet}>{query ? 'No match' : 'None published yet'}</div>}
          {customShown.map(w => row({
            item: { key: `custom:${w.name}`, kind: 'custom', id: w.name, title: w.title || w.name, meta: `Custom widget · v${w.publishedVersion}` },
            meta: busy === w.name ? 'adding…' : `v${w.publishedVersion}`,
            add: () => insertCustom(w),
            disabled: busy === w.name,
            data: { 'data-insert-custom': w.name },
            tooltip: w.description || w.name
          }))}
        </>
      ))}
      {section('All widgets', allWidgets.length, allWidgets.map(s => row({
        item: { key: `widget:${s.id}`, kind: 'widget', id: s.id, title: s.title, meta: s.id === AI_WIDGET ? 'Opens for your AI tool’s code' : 'Built-in widget', ...(s.id === AI_WIDGET ? { blank: AI_BLANK } : {}) },
        add: s.id === AI_WIDGET ? insertAi : () => insertBuiltIn(s),
        data: { 'data-insert-widget': s.id },
        tooltip: s.id === AI_WIDGET ? 'Adds a section and opens it, ready for the reply from your AI tool' : undefined
      })))}
      {section('Presets', presetsShown.length, (
        <>
          {!presetsShown.length && <div className={c.quiet}>{query ? 'No match' : 'None yet'}</div>}
          {presetsShown.map((p) => {
            const n = pagesUsing.get(String(p._id));
            const used = n === undefined ? (usage.data ? 'unused' : '') : `${n} ${n === 1 ? 'page' : 'pages'}`;
            return row({
              item: { key: `preset:${String(p._id)}`, kind: 'preset', id: String(p._id), title: p.title, meta: `Preset${used ? ` · ${used === 'unused' ? 'not used yet' : `on ${used}`}` : ''}` },
              meta: used,
              add: () => onInsert(presetInsert(p)),
              data: { 'data-insert-preset': String(p._id) },
              tooltip: p.widgetId
            });
          })}
        </>
      ))}
      {(peek || pre) && (
        <SectionPeek
          pageId={pageId}
          item={(peek || pre) as PeekItem}
          hidden={!peek}
          anchor={(root.current && root.current.closest<HTMLElement>('[data-panel]')) || root.current}
          placement={placement}
          onAdd={() => { if (!peek) return; const { add } = peek; setPeek(null); add(); }}
          onClose={closePeek}
        />
      )}
    </div>
  );
};

export default InsertPanel;
